'use client';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LINKS from '../constants/links';
import sendGtagEvent from '../utils/send-gtag-event';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookieConsent');
    if (consentGiven === null) {
      setVisible(true);
    } else {
      // Reapply stored consent to GTM & Zaraz
      handleStoredConsent(consentGiven);
    }

    // Listen for manual reopen (Manage Preferences)
    window.addEventListener('showCookieConsentDialog', handleShowDialog);

    return () => {
      window.removeEventListener('showCookieConsentDialog', handleShowDialog);
    };
  }, []);

  const handleShowDialog = () => setVisible(true);

  const handleStoredConsent = (consent) => {
    if (typeof window === 'undefined') return;
    const granted = consent === 'accepted';
    updateConsent(granted);
  };

  const updateConsent = (granted) => {
    // Google Consent Mode v2 Update
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookie_consent_update',
      ad_storage: granted ? 'granted' : 'denied',
      ad_user_data: granted ? 'granted' : 'denied',
      ad_personalization: granted ? 'granted' : 'denied',
      analytics_storage: granted ? 'granted' : 'denied',
      functionality_storage: 'granted',
      personalization_storage: granted ? 'granted' : 'denied',
      security_storage: 'granted',
    });

    // Cloudflare Zaraz fallback (falls aktiv)
    if (window.zaraz && window.zaraz.consent) {
      window.zaraz.consent.setAll(granted);
      if (granted) window.zaraz.consent.sendQueuedEvents();
    }
  };

  const consentAll = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cookieConsent', 'accepted');
      updateConsent(true);

      // GA4 Event senden
      sendGtagEvent('cookie_consent', { 
        action: 'accept_all',
        consent_timestamp: new Date().toISOString()
      });
      
      setVisible(false);
    }
  };

  const rejectAll = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cookieConsent', 'rejected');
      updateConsent(false);
      
      // GA4 Event senden (auch bei Ablehnung erlaubt)
      sendGtagEvent('cookie_consent', { 
        action: 'reject_all',
        consent_timestamp: new Date().toISOString()
      });
      
      setVisible(false);
    }
  };

  if (!mounted || !visible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 max-w-[625px] rounded-lg border border-[rgba(34,36,42,1)] bg-[#0d0e12] p-4 shadow-lg md:left-4 md:right-auto">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
        <p className="text-sm font-light leading-[1.5] text-gray-300 flex-1">
          Wir verwenden Cookies, um unsere Dienste zu verbessern.
          <span className="hidden sm:inline"><br/></span>
          <span className="sm:hidden"> </span>
          Lerne mehr in unserer{' '}
          <Link
            className="whitespace-nowrap font-normal text-white underline underline-offset-4 transition-opacity duration-300 hover:opacity-80 hover:text-white"
            to={LINKS.cookiePolicy}
          >
            Cookie-Richtlinie
          </Link>
          .
        </p>
        <div className="flex gap-x-3.5 justify-end">
          <button
            className="relative overflow-hidden bg-white text-black font-medium whitespace-nowrap shadow-[0px_4px_12px_rgba(255,255,255,0.1)] hover:shadow-[0px_4px_24px_rgba(255,255,255,0.2)] hover:bg-gray-300 transition-all duration-300 rounded-lg px-3 py-1.5 text-xs cursor-pointer"
            onClick={rejectAll}
          >
            Ablehnen
          </button>
          <button
            className="relative overflow-hidden bg-white text-black font-medium whitespace-nowrap shadow-[0px_4px_12px_rgba(255,255,255,0.1)] hover:shadow-[0px_4px_24px_rgba(255,255,255,0.2)] hover:bg-gray-300 transition-all duration-300 rounded-lg px-3 py-1.5 text-xs cursor-pointer"
            onClick={consentAll}
          >
            Akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;